import * as ents from '@attain-apps/entities'

/**
 * Type alias for Variable. While it may seem redundant now, it provides
 * flexibility for future enhancements. If we extend the variable Definition
 * later, using a type alias ensures we won't need to update the type in
 * multiple places.
 */
export type Definition = ents.core.dms.data.query.Variable
export namespace Definition {
  /**
   * Context for the variables holder
   */
  export type Holder = ExactlyOneOf<{
    session: Holder.ForSessionVariables
    scenario: Holder.ForScenarioVariables
    page: Holder.ForPageVariables
    widget: Holder.ForWidgetVariables
  }>
  export namespace Holder {
    /**
     * Used to encode context for variable definitions holder
     *
     *  __NOTE__: Since widgetHolder extends pageHolder, passing it as a page
     *  holder will result in only the necessary data for pageHolder being
     *  extracted, ensuring compliance with the Liskov Substitution Principle.
     */
    export function stringify({
      session,
      scenario,
      page,
      widget,
    }: Holder): string {
      if (session) {
        return ForSessionVariables.stringify(session)
      }
      if (scenario) {
        return ForScenarioVariables.stringify(scenario)
      }
      if (page) {
        return ForPageVariables.stringify(page)
      }
      if (widget) {
        return ForWidgetVariables.stringify(widget)
      }
      return 'unknown'
    }
    export interface ForSessionVariables {
      /**
       * __NOTE:__ Session variables are store on the {@link ents.core.admin.App}
       */
      appId: ents.core.admin.App.Id
    }
    export namespace ForSessionVariables {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({ appId }: ForSessionVariables): string {
        return `session_${appId}`
      }
    }

    export interface ForScenarioVariables {
      /**
       * __NOTE:__ Scenario variables are store on the {@link ents.core.admin.App}
       */
      appId: ents.core.admin.App.Id
    }
    export namespace ForScenarioVariables {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({ appId }: ForScenarioVariables): string {
        return `scenario_${appId}`
      }
    }

    export interface ForPageVariables {
      pageId: ents.core.mosaic.Page.Id
    }
    export namespace ForPageVariables {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({ pageId }: ForPageVariables): string {
        return `page_${pageId}`
      }
    }

    export interface ForWidgetVariables {
      widgetId: ents.core.mosaic.Widget.Id
    }
    export namespace ForWidgetVariables {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function stringify({ widgetId }: ForWidgetVariables): string {
        return `widget_${widgetId}`
      }
    }
  }

  /**
   * Lookup of stringified variables holder and its variable definitions
   */
  export type Lookup = Partial<Record<string, readonly Definition[]>>

  /**
   * Attempts to cast the default value of a variable definition based on the
   * type.
   *
   * If the type exists in the provided schema, the default value is cast using
   * the schema's type information. Otherwise, the original default value is
   * retained.
   */
  export function castValue(
    definition: Definition,
    schema: ents.core.dms.models.Schema,
  ): Definition {
    const type = schema.tryGetType(definition.type)
    return {
      ...definition,
      defaultValue: type
        ? ents.core.common.value.Raw.cast(definition.defaultValue, type)
        : definition.defaultValue,
    }
  }
}
